// frontend/src/components/concierge/tabs/OverviewTab.jsx
// Overview: Profile + Do next, an Engagement card above Activity, and the unified activity stream with a
// three-mode composer. Every mode posts through the same endpoint + helpers the
// dedicated tab uses (spec "Overview composer") — no second implementation.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ActivityRow from './ActivityRow';
import ScorePicker from './ScorePicker';
import ProfileCard from './ProfileCard';
import ListsCard from './ListsCard';
import { hudlAgoText, hudlProblem, hudlUpdateTone } from '../../../lib/hudlModel';
import {
  ACTIVITY_FILTERS, buildActivity, filterActivity, fmtMonDay,
  agoDate, lastEditSub, loginTone,
} from '../../../lib/conciergeActivityModel';
import {
  athletePhone, buildFollowUpPayload, buildStartPayload, defaultIncludeCell, guardianCheckboxes,
  hasStartRecipient, ownerInfo, pickThreadFor, showIncludeCell,
} from '../../../lib/smsThreadModel';

const MODES = [['text', 'Text family'], ['note', 'Internal note'], ['wellness', 'Wellness check-in']];
// One Engagement row, in the Details tab's key/value style; `sub` is a
// muted second line under the label (who edited, which reel).
function EngageRow({ label, value, tone, sub }) {
  return (
    <div className="cn-row cn-kv">
      <span className="cn-kv__label">{label}{sub && <span className="cn-kv__sub">{sub}</span>}</span>
      <span className={`cn-kv__value cn-tone--${tone}`}>{value}</span>
    </div>
  );
}

const hudlAgoDate = (ago, iso) => `${ago} · ${fmtMonDay(iso)}`;

export default function OverviewTab(props) {
  const { userId, cfg, API_URL, athleteName, profile, lastLoginAt, lastProfileChangeAt, lastChange, doNext,
    onEditProfile, notes, smsRecent, mentalChecks, todoHistory, onGoTab, onAddNote, onSaveCheck, onSmsSent } = props;
  const now = new Date();
  const [filter, setFilter] = useState('all');
  const [mode, setMode] = useState('text');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [recip, setRecip] = useState(null);
  const [recipFailed, setRecipFailed] = useState(false);
  const [includeAthlete, setIncludeAthlete] = useState(true);
  const [slots, setSlots] = useState([]);
  const [threads, setThreads] = useState(null);   // null = unknown (not loaded / failed)
  const [vis, setVis] = useState({ visible_to_athlete: false, visible_to_coach: false });
  const [mental, setMental] = useState(0);
  const [sleep, setSleep] = useState(0);
  const [activities, setActivities] = useState([]);   // logged calls/meetings; a failed fetch leaves the stream as is
  const [hudl, setHudl] = useState(null);   // null = loading; its own fetch so nothing else waits on Hudl
  useEffect(() => {
    axios.get(`${API_URL}/concierge/${userId}/hudl`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(res => setHudl(res.data)).catch(() => setHudl({ status: 'hudl_unavailable' }));
  }, [userId, API_URL]);

  useEffect(() => {
    axios.get(`${API_URL}/concierge/${userId}/activities?limit=50`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(res => setActivities(Array.isArray(res.data) ? res.data : []))
      .catch(() => setActivities([]));
  }, [userId, API_URL]);

  useEffect(() => {
    // Auth header read here (not from the per-render `cfg` prop) so the deps stay honest.
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    const threadsP = axios.get(`${API_URL}/concierge/${userId}/sms/threads`, { headers })
      .then(res => res.data?.threads || []).catch(() => null);
    Promise.all([axios.get(`${API_URL}/concierge/${userId}/sms/recipients`, { headers }), threadsP])
      .then(([res, list]) => {
        // Default "To:" is the family on the newest OPEN thread, so the default send
        // continues that conversation; athlete + every guardian only when none is open.
        const available = guardianCheckboxes(res.data).map(g => g.slot);
        const open = (list || []).find(t => t.status !== 'closed');
        const roles = open ? (open.participants || []).map(p => p.role) : null;
        setRecip(res.data);
        setThreads(list);
        setIncludeAthlete(roles ? roles.includes('athlete') : true);
        setSlots(roles ? available.filter(s => roles.includes(s)) : available);
      })
      .catch(() => { setRecip(null); setRecipFailed(true); });
  }, [userId, API_URL]);

  const items = useMemo(() => buildActivity({ notes, smsMessages: smsRecent, mentalChecks, todoHistory, activities }),
    [notes, smsRecent, mentalChecks, todoHistory, activities]);
  const shown = filterActivity(items, filter);
  const guardians = guardianCheckboxes(recip);
  const owner = ownerInfo(recip);
  const athleteHasPhone = recip ? !!recip.athlete_has_phone : !!athletePhone(profile);
  const firstName = (athleteName || '').split(' ')[0];
  const wantAthlete = includeAthlete && athleteHasPhone;
  const isNewThread = Array.isArray(threads) && !pickThreadFor(threads, { includeAthlete: wantAthlete, guardianSlots: slots });
  const placeholder = mode === 'text'
    ? `${isNewThread ? 'New text to' : 'Reply to'} ${[includeAthlete && athleteHasPhone && firstName, ...guardians.filter(g => slots.includes(g.slot)).map(g => g.name.split(' ')[0])].filter(Boolean).join(', ') || '…'}…`
    : (mode === 'note' ? 'Write a note…' : 'Notes on mental state / sleep…');

  const done = (p) => p.then(() => { setDraft(''); setMental(0); setSleep(0); }).catch(e => {
    // Note/Wellness: the page already shows its banner and rethrows; only Text needs the inline message.
    if (mode === 'text') setErr(e?.response?.data?.detail || 'Failed to send.');
  }).finally(() => setBusy(false));

  const send = () => {
    setErr('');
    const body = draft.trim();
    if (mode === 'note') {
      if (!body) return;
      setBusy(true);
      done(onAddNote(body, vis).then(() => setVis({ visible_to_athlete: false, visible_to_coach: false })));
      return;
    }
    if (mode === 'wellness') {
      if (!mental && !sleep && !body) return;
      setBusy(true);
      done(onSaveCheck({ mental_score: mental || null, sleep_score: sleep || null, notes: body || null }));
      return;
    }
    if (!body) return;
    setBusy(true);
    done(axios.get(`${API_URL}/concierge/${userId}/sms/threads`, cfg).then(res => {
      const list = res.data?.threads || [];
      const thread = pickThreadFor(list, { includeAthlete: wantAthlete, guardianSlots: slots });
      return thread
        ? axios.post(`${API_URL}/concierge/${userId}/sms/threads/${thread.id}/messages`, buildFollowUpPayload({ body }), cfg)
        : axios.post(`${API_URL}/concierge/${userId}/sms/threads`, buildStartPayload({
          includeAthlete: wantAthlete, guardianSlots: slots,
          includeCell: showIncludeCell(recip) && defaultIncludeCell(slots.length > 0), body }), cfg)
          // The new thread now exists, so the placeholder reads "Reply to" from here on.
          .then(r => { setThreads([{ status: 'active', participants: [
            ...(wantAthlete ? [{ role: 'athlete' }] : []), ...slots.map(role => ({ role }))] }, ...list]); return r; });
    }).then(() => { if (onSmsSent) onSmsSent(); }));
  };

  const canSendText = hasStartRecipient({ includeAthlete, athleteHasPhone, guardianSlots: slots });
  const toggleSlot = (s) => setSlots(p => (p.includes(s) ? p.filter(x => x !== s) : [...p, s]));

  const hudlOk = hudl?.status === 'ok';
  const hudlWait = hudl === null;
  const hudlDash = hudlWait ? <><span className="cn-inline-spin" aria-hidden="true" />Loading…</> : '—';
  const hudlWhy = hudlWait ? '' : hudlProblem(hudl.status);

  return (
    <div className="cn-tabbody">
      <div className="cn-overview-grid">
        <div className="cn-tabbody">
          <ProfileCard userId={userId} onEditProfile={onEditProfile} />
          <section className="cn-card">
            <div className="cn-card__head">
              <h2 className="cn-card__title">Do next</h2>
              <span className="cn-card__sub">To-dos and alerts</span>
              <button type="button" className="cn-card__action" onClick={() => onGoTab('todo')}>All to-dos →</button>
            </div>
            {doNext.length === 0 ? <div className="cn-empty-line">Nothing on the list.</div> : doNext.map(r => (
              <button type="button" key={r.key} className="cn-row cn-donext" onClick={() => onGoTab(r.taskId ? 'todo' : 'wellness')}>
                <span className="cn-donext__box" aria-hidden="true" />
                <span className="cn-donext__text"><span className="cn-donext__title">{r.title}</span>
                  {r.sub && <span className="cn-donext__sub">{r.sub}</span>}</span>
                <span className={`cn-pill cn-pill--${r.tone}`}>{r.pill}</span>
              </button>
            ))}
          </section>
        </div>
        <div className="cn-tabbody">
          <section className="cn-card" aria-label="Engagement">
            <div className="cn-card__head"><h2 className="cn-card__title">Engagement</h2></div>
            <EngageRow label="Last login" value={lastLoginAt ? agoDate(lastLoginAt, now) : 'Never'}
              tone={loginTone(lastLoginAt, now)} />
            <EngageRow label="Last profile edit"
              value={lastProfileChangeAt ? agoDate(lastProfileChangeAt, now) : 'Never'} tone="ink"
              sub={lastProfileChangeAt ? lastEditSub(lastChange?.field, lastChange?.by) : ''} />
            <EngageRow label="Last Hudl update"
              value={hudlOk ? (hudl.last_update_at ? hudlAgoDate(hudlAgoText(hudl.last_update_at, now), hudl.last_update_at) : 'Never') : hudlDash}
              tone={hudlOk ? hudlUpdateTone(hudl.last_update_at, now) : 'muted'}
              sub={hudlOk ? (hudl.last_update_title || '') : hudlWhy} />
            <EngageRow label="Last seen on Hudl"
              value={hudlOk ? (hudl.last_seen_at ? hudlAgoDate(hudlAgoText(hudl.last_seen_at, now), hudl.last_seen_at) : 'Not available') : hudlDash}
              tone={hudlOk && hudl.last_seen_at ? 'ink' : 'muted'}
              sub={hudlOk ? '' : hudlWhy} />
          </section>
          <ListsCard userId={userId} API_URL={API_URL} cfg={cfg} />
          <section className="cn-card">
            <div className="cn-card__head">
              <h2 className="cn-card__title">Activity</h2>
              <div className="cn-fpills">
                {ACTIVITY_FILTERS.map(f => (
                  <button key={f.id} type="button" aria-pressed={filter === f.id}
                    className={`cn-fpill${filter === f.id ? ' cn-fpill--on' : ''}`} onClick={() => setFilter(f.id)}>{f.label}</button>
                ))}
              </div>
            </div>
            <div className="cn-composer2">
              <div className="cn-seg" role="group" aria-label="Composer mode">
                {MODES.map(([id, label]) => (
                  <button key={id} type="button" aria-pressed={mode === id}
                    className={`cn-seg__btn${mode === id ? ' cn-seg__btn--on' : ''}`} onClick={() => { setMode(id); setErr(''); }}>{label}</button>
                ))}
              </div>
              <textarea className="cn-textarea" rows={2} placeholder={placeholder} value={draft} onChange={e => setDraft(e.target.value)} />
              <div className="cn-composer2__foot">
                {mode === 'text' && (
                  <>
                    <span className="cn-composer2__label">To:</span>
                    {athleteHasPhone && (
                      <button type="button" aria-pressed={includeAthlete} onClick={() => setIncludeAthlete(v => !v)}
                        className={`cn-chip-t${includeAthlete ? ' cn-chip-t--on' : ''}`}>{firstName || 'Athlete'}{includeAthlete ? ' ✓' : ''}</button>
                    )}
                    {guardians.map(g => (
                      <button key={g.slot} type="button" aria-pressed={slots.includes(g.slot)} onClick={() => toggleSlot(g.slot)}
                        className={`cn-chip-t${slots.includes(g.slot) ? ' cn-chip-t--on' : ''}`}>{g.name}{slots.includes(g.slot) ? ' ✓' : ''}</button>
                    ))}
                    {owner.name && <span className="cn-composer2__label">+ {owner.isMe ? 'you' : owner.name} (owner)</span>}
                    <button type="button" className="cn-pbtn cn-composer2__send" onClick={send}
                      disabled={busy || !draft.trim() || !canSendText || !owner.textable}>Send text</button>
                  </>
                )}
                {mode === 'note' && (
                  <>
                    <span className="cn-composer2__label">Share with:</span>
                    {[['visible_to_athlete', 'Athlete/Guardian'], ['visible_to_coach', 'Coach']].map(([k, l]) => (
                      <button key={k} type="button" aria-pressed={vis[k]} onClick={() => setVis(v => ({ ...v, [k]: !v[k] }))}
                        className={`cn-chip-t${vis[k] ? ' cn-chip-t--on' : ''}`}>{l}</button>
                    ))}
                    <button type="button" className="cn-pbtn cn-composer2__send" onClick={send} disabled={busy || !draft.trim()}>Add note</button>
                  </>
                )}
                {mode === 'wellness' && (
                  <>
                    <ScorePicker label="Mental" value={mental} onChange={setMental} compact />
                    <ScorePicker label="Sleep" value={sleep} onChange={setSleep} compact />
                    <button type="button" className="cn-pbtn cn-composer2__send" onClick={send}
                      disabled={busy || (!mental && !sleep && !draft.trim())}>Save check-in</button>
                  </>
                )}
              </div>
              {err && <span className="cn-inline-msg cn-inline-msg--err">{err}</span>}
              {mode === 'text' && recipFailed && (
                <span className="cn-inline-msg cn-inline-msg--err">Couldn't load recipients. Reload the page to try again.</span>)}
              {mode === 'text' && !owner.textable && owner.blockMessage && (
                <span className="cn-inline-msg cn-inline-msg--err">{owner.blockMessage}</span>)}
            </div>
            {shown.length === 0 ? <div className="cn-empty-line">Nothing here yet.</div>
              : shown.map(it => <ActivityRow key={it.id} item={it} now={now} />)}
          </section>
        </div>
      </div>
    </div>
  );
}
